/**
 * 从提示词里读回「这个模特是谁」—— **唯一实现**。
 *
 * 为什么是解析而不是加字段
 * ------------------------
 * 模特设定（目标市场 / 性别 / 年龄 / 体型 / 气质 / 发型）在生成那一刻就被
 * 拼进了提示词 —— 拼装真源是 `config/modelProfile.ts` 的 `modelLookText()`：
 *
 *   ```
 *   · 模特设定：目标市场：日本；性别：女；年龄：18-24；模特气质：成熟
 *   ```
 *
 * 也就是说**提示词里已经存下了全部信息**，只是散在一行文本里。而 `works` 表
 * 没有这些列，历史记录也补不回来（模型没说当时选了什么）。
 * 所以 AI 模特库不新增存储，直接读回这一行 —— 好处是**存量记录立刻可筛选**，
 * 代价是格式必须稳住。稳住的办法见下面 `MODEL_LOOK_TAGS` 的注释。
 *
 * 🔴 解析失败**不许猜**：读不出来就如实返回空 + 原文，
 * 卡片上显示「未记录设定」而不是编一个「女 / 25 岁」出来。
 * 本项目的规矩是「口径不许撒谎」——筛选器把人筛丢，比没有筛选器更坏。
 */

import { FEATURE_SETS } from '@/config/catalog'
import { MODEL_LOOK_FIELDS, type ModelLookKey } from '@/config/modelProfile'

export type ModelLookTag = {
  key: ModelLookKey
  label: string
  value: string
}

export type ModelProfile = {
  /** 结构化设定。能解出几个就有几个，顺序跟 `MODEL_LOOK_FIELDS` 一致 */
  tags: ModelLookTag[]
  /**
   * 解不出结构化设定时的原文（去掉「· 」前缀）。
   *
   * 三种来路：
   *   · 老格式 `· 模特与场景要求：东亚女性`（2026-09-30 之前的那一版）
   *   · 服饰图「模特生成」档 —— `· 模特描述：25岁亚洲女模特，短发，…` 是**自由文本**，
   *     天生没有 key/value 结构，别硬拆（拆出来的「25岁亚洲女模特」既不是年龄也不是人种）
   *   · 什么都没有 → 空串
   */
  note: string
}

/* -------------------------------------------------------------------------- */
/* 解析                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 带值的设定行前缀 —— 三种写法都认，**顺序即优先级**。
 *
 * 「模特设定」必须排第一：新版 prompt 里同时存在这一行与引用它的【执行要求】
 * 段落（「1. 人物以【模特设定】为准…」），先匹配长的那个才不会截错。
 */
const LOOK_PREFIXES = ['模特设定', '模特与场景要求', '模特描述'] as const

/** 全角/半角冒号都认：模板里是全角，但别让一个符号把整页筛选打成空的 */
const COLON = /[:：]/

/** 值里的分隔符 —— 与 `modelProfile.modelLookText()` 的 `join('；')` 成对 */
const SEP = '；'

/**
 * 从提示词里抠出那个「模特设定」行（去掉 `· ` 前缀与首尾空白）。
 *
 * 逐行扫而不是整篇正则：提示词里 `模特` 这个词出现十几次（任务名、执行要求、
 * 禁止项都有），只有**行首紧跟前缀 + 冒号**的那一行才是设定本身。
 */
function lookLine(prompt: string): { prefix: string; body: string } | null {
  for (const raw of prompt.split('\n')) {
    const line = raw.replace(/^[\s·•]*/, '').trim()
    for (const prefix of LOOK_PREFIXES) {
      if (!line.startsWith(prefix)) continue
      const rest = line.slice(prefix.length)
      const m = rest.match(COLON)
      if (!m) continue
      const body = rest.slice((m.index ?? 0) + m[0].length).trim()
      if (body) return { prefix, body }
    }
  }
  return null
}

/**
 * 拼装侧用的 label → key 反查表。
 *
 * ⚠️ 这份表**不是第二份枚举**：它唯一的输入是 `MODEL_LOOK_FIELDS`，
 * 只是把 label 归一成 key 好排序。label 是「模特气质」而 `modelProfile.ts`
 * 的注释里写作「气质」，所以额外认一个去掉「模特」前缀的写法 ——
 * 旧 prompt（2026-09-30 那一版）写的就是「气质：自然」。
 */
const LABEL_TO_KEY: { key: ModelLookKey; label: string }[] = MODEL_LOOK_FIELDS.map(
  (f) => ({ key: f.key, label: f.label }),
)

function keyOfLabel(rawKey: string): { key: ModelLookKey; label: string } | null {
  const k = rawKey.trim()
  for (const item of LABEL_TO_KEY) {
    if (item.label === k) return item
  }
  // 「气质」这类简写：去前缀后再比一次
  for (const item of LABEL_TO_KEY) {
    if (item.label.replace(/^模特/, '') === k) return item
  }
  return null
}

/**
 * 解析提示词 → 模特设定。
 *
 * 结构化解析与原文**互斥**：解出了一条以上 key/value 就说明这一行是结构化的，
 * 此时 `note` 留空（卡片渲染标签就够了，再贴一遍原文是重复）。
 */
export function parseModelLook(prompt?: string): ModelProfile {
  const text = (prompt ?? '').trim()
  if (!text) return { tags: [], note: '' }

  const hit = lookLine(text)
  if (!hit) return { tags: [], note: '' }

  const pairs: ModelLookTag[] = []
  for (const seg of hit.body.split(SEP)) {
    const m = seg.match(COLON)
    if (!m || m.index === undefined) continue
    const key = keyOfLabel(seg.slice(0, m.index))
    const value = seg.slice(m.index + m[0].length).trim()
    if (key && value) pairs.push({ key: key.key, label: key.label, value })
  }

  if (pairs.length) {
    // 按 `MODEL_LOOK_FIELDS` 的顺序重排（市场 → 性别 → 年龄 → 体型 → 气质 → …），
    // 跟生成页上的选择顺序、提示词里的书写顺序都保持一致
    const order = new Map(MODEL_LOOK_FIELDS.map((f, i) => [f.key, i]))
    pairs.sort((a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99))
    return { tags: pairs, note: '' }
  }

  return { tags: [], note: hit.body }
}

/* -------------------------------------------------------------------------- */
/* 筛选项（facet）                                                              */
/* -------------------------------------------------------------------------- */

export type ModelFacetOption = { value: string; count: number }
export type ModelFacet = {
  key: ModelLookKey
  label: string
  options: ModelFacetOption[]
}

/**
 * 从**已加载的记录**里统计出可用的筛选项。
 *
 * 🔴 选项清单从数据来，不写死枚举 —— 理由有两条：
 *   1. 写死「非洲 / 拉美」这种没人用过的档，点下去永远是空列表（筛了个寂寞）；
 *   2. `modelProfile.ts` 的选项表是**生成端**的真源，筛选端再抄一份就是
 *      两份枚举，早晚分叉（改了生成端档位，筛选端还在拿旧值比）。
 *
 * 顺序仍按 `MODEL_LOOK_FIELDS` → `field.options`：既保证「市场在性别前」这种
 * 稳定排列（每次进来 chip 不跳位），又不用在数据里排一遍。
 */
export function buildModelFacets(profiles: ModelProfile[]): ModelFacet[] {
  const fields = MODEL_LOOK_FIELDS
  return fields
    .map((f) => {
      const counter = new Map<string, number>()
      for (const p of profiles) {
        const tag = p.tags.find((t) => t.key === f.key)
        if (!tag) continue
        counter.set(tag.value, (counter.get(tag.value) ?? 0) + 1)
      }
      // 已知档位在前、按枚举顺序；枚举里没有的（历史写法的自由值）垫在后面
      const known = f.options.filter((o) => counter.has(o))
      const unknown = [...counter.keys()].filter((v) => !f.options.includes(v)).sort()
      const options = [...known, ...unknown].map((v) => ({ value: v, count: counter.get(v) ?? 0 }))
      return { key: f.key, label: f.label, options }
    })
    .filter((f) => f.options.length > 0)
}

/**
 * `app_slug` → 产出档的显示名（`product-product-model` → 「AI 模特图」）。
 *
 * 名字**从 `catalog.ts` 查**，不在页面里再写一份 —— 生成页改了档名，
 * 这里要跟着变，两处各写就会看到「AI 模特图」和「AI 模特写真」并存。
 *
 * `app_slug` 的拼法是 `family-featureId`，而 `featureId` 自己就带 `-`
 * （`product-model`），所以按**第一个** `-` 切，不是 `split('-')`。
 * `FEATURE_SETS` 的键沿用参考站 IA（`products` / `fashion`），不是 `product`。
 */
export function sourceLabelOf(appSlug: string): string {
  const slug = (appSlug ?? '').trim()
  const i = slug.indexOf('-')
  if (i <= 0 || i === slug.length - 1) return slug
  const family = slug.slice(0, i)
  const featureId = slug.slice(i + 1)
  const setKey = family === 'product' ? 'products' : family
  const hit = (FEATURE_SETS[setKey] ?? []).find((f) => f.id === featureId)
  return hit?.name ?? slug
}
